import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';

const SOURCE_TYPES = [
  'WAREHOUSE_TANK',
  'SITE_TANK',
  'BOWSER',
  'FUEL_STATION',
  'SUPPLIER_DIRECT',
  'EXTERNAL_STATION',
  'OTHER',
];

const PmvFuelSourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [activeOnly, setActiveOnly] = useState<boolean | undefined>(true);
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [form] = Form.useForm();

  const q = useQuery({
    queryKey: ['pmv-fuel-sources'],
    queryFn: () => pmvService.listFuelSources(),
  });

  const items = (q.data?.items ?? []).filter((r: { is_active?: boolean; source_type?: string }) => {
    if (activeOnly === true && r.is_active === false) return false;
    if (activeOnly === false && r.is_active !== false) return false;
    if (typeFilter && r.source_type !== typeFilter) return false;
    return true;
  });

  const create = useMutation({
    mutationFn: (body: { name: string; source_type: string; code?: string }) =>
      pmvService.createFuelSource(body),
    onSuccess: () => {
      message.success('Fuel source created (reference only — no stock balance)');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['pmv-fuel-sources'] });
    },
  });

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fuel Sources / Bowsers"
        subtitle="Operational source profiles referencing Inventory warehouse or PMV bowser equipment — no shadow stock ledger"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => setOpen(true)}>
              Add source
            </Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/control-tower')}>Control Tower</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel')}>Fuel Register</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 180 }}
              value={typeFilter}
              onChange={setTypeFilter}
              options={SOURCE_TYPES.map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              placeholder="Active"
              style={{ width: 120 }}
              value={activeOnly === undefined ? undefined : activeOnly ? '1' : '0'}
              onChange={(v) => setActiveOnly(v === undefined ? undefined : v === '1')}
              options={[
                { value: '1', label: 'Active' },
                { value: '0', label: 'Inactive' },
              ]}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Bowser may be both PMV equipment and a fuel delivery source. Capacity here is operational metadata only."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No fuel sources match these filters">
          <Button type="primary" onClick={() => setOpen(true)}>
            Add source
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: 'Number', dataIndex: 'source_number', width: 120 },
            { title: 'Name', dataIndex: 'name', ellipsis: true },
            {
              title: 'Type',
              dataIndex: 'source_type',
              width: 150,
              render: (t) => <Tag>{t}</Tag>,
            },
            { title: 'Code', dataIndex: 'code', width: 120 },
            {
              title: 'Capacity (L)',
              dataIndex: 'capacity_litres',
              width: 120,
              render: (v) => (v == null ? '—' : Number(v).toFixed(0)),
            },
            {
              title: 'Active',
              dataIndex: 'is_active',
              width: 90,
              render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
            },
          ]}
        />
      )}

      <Modal
        title="Create fuel source"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => create.mutate(values)}
          initialValues={{ source_type: 'BOWSER' }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="source_type" label="Source type" rules={[{ required: true }]}>
            <Select options={SOURCE_TYPES.map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input />
          </Form.Item>
          <Form.Item name="capacity_litres" label="Capacity (litres, metadata only)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvFuelSourcesPage;
